import { CircularProgress, Divider, Grid, Paper, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useLocation, useNavigate } from 'react-router';
import { useCallback, useState, useEffect } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import FilledBtn from 'components/buttons/FilledBtn';
import { toast } from 'react-toastify';
import api from 'api';
import ProfileModal from 'components/modal/ProfileModal';
import { useSocket } from 'hooks/useSocket';

const SupportTicket = () => {
  const socket = useSocket();
  const [responseData, setResponseData] = useState([]);
  const [responseLoader, setResponseLoader] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const ticket = location.state?.ticket;

  const fetchSupportTicketsData = useCallback(async () => {
    if (!ticket?._id) return;

    setResponseLoader(true);
    try {
      const { data } = await api.get(`support/tickets/${ticket._id}`);
      setResponseData(data?.responses || []);
      console.log('Fetched ticket responses:', data?.responses);
    } catch (error) {
      console.error('Error fetching ticket responses:', error);
      // toast.error('Failed to fetch ticket responses.');
    } finally {
      setResponseLoader(false);
    }
  }, [ticket?._id]);

  const handleSendResponse = async (payload) => {
    try {
      const { data } = await api.post('support/responses', payload);

      // const updatedResponse = {
      //   ...data,
      //   responderId: {
      //     _id: data.responderId,
      //     name: localStorage.getItem('userName'),
      //     role: localStorage.getItem('role')
      //   }
      // };
      // console.log('Added Object:', updatedResponse);
      // setResponseData((prevData) => [...prevData, updatedResponse]);
      setMessage('');
      toast.success('Response sent successfully!');
    } catch (error) {
      console.error('Error sending response:', error);
      toast.error('Failed to send your message.');
    }
  };

  const handleSend = () => {
    if (ticket?.status === 'CLOSED') {
      toast.error('This ticket is closed. You cannot send a message.');
      return;
    }
    const trimmedMessage = message.trim();

    if (!trimmedMessage) {
      toast.error('Message cannot be empty');
      return;
    }
    const payload = {
      ticketId: ticket?._id,
      message: message,
      isStaffResponse: true
    };
    handleSendResponse(payload);
  };

  const handleProfileModalClose = () => {
    setProfileModalOpen(false);
  };

  useEffect(() => {
    fetchSupportTicketsData();
  }, [fetchSupportTicketsData]);
  useEffect(() => {
    if (socket) {
      socket.on(ticket._id, (msg) => {
        console.log('Received from server:', msg);
        setResponseData((prevData) => [...prevData, msg]);
      });
    }
  }, [socket]);
  // const userRole = localStorage.getItem('role');

  if (!ticket) {
    return (
      <Box
        sx={{
          backgroundColor: 'white',
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography variant="h5">No messages available.</Typography>
      </Box>
    );
  }

  if (!ticket?._id) {
    return (
      <Box
        sx={{
          backgroundColor: 'white',
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress sx={{ color: '#420074' }} />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#f9f9f9',
          color: '#333',
          display: 'flex',
          flexDirection: 'column',
          padding: '24px',
          borderRadius: '15px',
          boxShadow: '7px 7px 10px rgba(0, 0, 0, 0.1), inset -5px -5px 5px rgba(255, 255, 255, 0.9)'
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          {/* Header row styling: Back button on the left, title in center */}
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 16px'
              }}
            >
              <FilledBtn
                btnText={'Back'}
                bgColor={'#430074'}
                fontSize={'14px'}
                onClick={() => {
                  // setOpenModal(true);
                  navigate(-1);
                }}
              />
              {/* Spacer to center the title */}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '24px',
                    lineHeight: '36px',
                    fontWeight: 500,
                    color: '#333'
                  }}
                >
                  Support Ticket
                </Typography>
              </div>
              <div style={{ width: '80px' }} /> {/* adjust to match your button's width */}
            </div>
            {/* Ticket ID Centered */}
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                marginTop: '8px'
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  fontWeight: 400,
                  color: '#666'
                }}
              >
                Ticket ID: {ticket._id}
              </Typography>
            </div>
          </div>

          <Paper
            elevation={3}
            sx={{
              backgroundColor: '#FFF', // White background for the card
              p: 4,
              mt: 4,
              borderRadius: '8px',
              width: '60%',
              boxShadow:
                '10px 10px 10px rgba(0, 0, 0, 0.1), -10px -10px 10px rgba(0, 0, 0, 0.1),inset -5px -5px 5px rgba(255, 255, 255, 0.9)'
            }}
          >
            <Grid container spacing={2}>
              {/* Ticket Details */}
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: '20px',
                    lineHeight: '30px',
                    fontWeight: 500,
                    color: '#333'
                  }}
                >
                  Title
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    lineHeight: '25px',
                    fontWeight: 400,
                    color: '#666'
                  }}
                >
                  {ticket.title}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: '20px',
                    lineHeight: '30px',
                    fontWeight: 500,
                    color: '#333'
                  }}
                >
                  Description
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    lineHeight: '25px',
                    fontWeight: 400,
                    color: '#666'
                  }}
                >
                  {ticket.description}
                </Typography>
              </Grid>

              {/* <Grid item xs={12}>
              <Typography
                sx={{
                  fontSize: '20px',
                  lineHeight: '30px',
                  fontWeight: 500,
                  color: '#333'
                }}
              >
                Sender Name
              </Typography>
              <Typography
                sx={{
                  fontSize: '16px',
                  lineHeight: '25px',
                  fontWeight: 400,
                  color: '#666'
                }}
              >
                {ticket.userId.name}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography
                sx={{
                  fontSize: '20px',
                  lineHeight: '30px',
                  fontWeight: 500,
                  color: '#333'
                }}
              >
                Created At
              </Typography>
              <Typography
                sx={{
                  fontSize: '16px',
                  lineHeight: '25px',
                  fontWeight: 400,
                  color: '#666'
                }}
              >
                {ticket.createdAt &&
                  new Date(ticket.createdAt).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  })}
              </Typography>
            </Grid> */}

              {/* Message Thread */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Divider sx={{ backgroundColor: '#E0E0E0', mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mx: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '20px',
                      lineHeight: '30px',
                      fontWeight: 500,
                      color: 'black',
                      mb: 2
                    }}
                  >
                    {ticket.userId.name}
                  </Typography>
                  <InfoIcon
                    sx={{ marginBottom: 2, color: '#420074', cursor: 'pointer' }}
                    onClick={() => {
                      console.log('User ID:', ticket.userId);
                      setProfileModalOpen(true);
                    }}
                  />
                </Box>
                <Divider sx={{ backgroundColor: '#E0E0E0', mb: 2 }} />
                {/* <Typography
                sx={{
                  fontSize: '20px',
                  lineHeight: '30px',
                  fontWeight: 500,
                  color: '#420074', // Purple for section header
                  mb: 2
                }}
              >
                Messages
              </Typography> */}
                {responseLoader ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '100px'
                    }}
                  >
                    <CircularProgress sx={{ color: '#420074' }} />
                  </Box>
                ) : responseData?.length === 0 && !responseLoader ? (
                  <Typography
                    sx={{
                      fontSize: '16px',
                      lineHeight: '25px',
                      fontWeight: 500,
                      color: '#666',
                      textAlign: 'center',
                      fontStyle: 'italic'
                    }}
                  >
                    No messages yet.
                  </Typography>
                ) : (
                  responseData?.map((message) => (
                    <Box
                      key={message._id}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: '8px',
                        backgroundColor: message.responderId.role === 'superAdmin' ? '#F3E5F5' : '#e3f2fd',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: message.responderId.role === 'superAdmin' ? 'flex-end' : 'flex-start',
                        maxWidth: '70%',
                        width: 'fit-content',
                        ml: message.responderId.role === 'superAdmin' ? 'auto' : 0,
                        mr: message.responderId.role === 'superAdmin' ? 0 : 'auto'
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '16px',
                          lineHeight: '25px',
                          fontWeight: 400
                          // color: message.responderId.role === 'superAdmin' ? '#430074' : '#2A5E3D'
                        }}
                      >
                        {message?.responderId?.role === 'superAdmin' ? (
                          <>
                            {' '}
                            {message.message} {' : '}
                            <span style={{ color: '#420074', fontWeight: 500 }}>
                              {message?.responderId?.name} ({message?.responderId?.role})
                            </span>
                          </>
                        ) : (
                          <>
                            <span style={{ color: '#420074', fontWeight: 500 }}>
                              {message?.responderId?.name} ({message?.responderId?.role}){' : '}
                            </span>
                            {message.message}
                          </>
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#999',
                          display: 'block',
                          mt: 0.5
                        }}
                      >
                        {new Date(message.createdAt).toLocaleString()}
                      </Typography>
                    </Box>
                  ))
                )}
                {ticket?.status === 'CLOSED' && (
                  <Typography
                    sx={{
                      fontSize: '16px',
                      lineHeight: '25px',
                      fontWeight: 400,
                      color: '#666',
                      fontStyle: 'italic',
                      textAlign: 'center',
                      mt: 2,
                      backgroundColor: '#f4c18d',
                      p: 2,
                      borderRadius: '8px',
                      maxWidth: '70%',
                      ml: 'auto',
                      mr: 0
                    }}
                  >
                    This ticket is closed. You cannot send any more messages.
                  </Typography>
                )}
              </Grid>
              {/* Input Field and Send Button */}
              <Grid item xs={12}>
                <Divider sx={{ backgroundColor: '#E0E0E0', mb: 2 }} />
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12}>
                    <TextField
                      label="Type your message"
                      multiline
                      rows={8}
                      fullWidth
                      variant="outlined"
                      value={message}
                      // onChange={handleResponseChange}
                      onChange={(e) => setMessage(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#FFF',
                          '& fieldset': {
                            borderColor: '#E0E0E0'
                          },
                          '&:hover fieldset': {
                            borderColor: '#420074'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#420074'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: '#666'
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#420074'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} container justifyContent="flex-end">
                    <FilledBtn
                      btnText={'Send Response'}
                      bgColor={'#430074'}
                      fontSize={'14px'}
                      onClick={handleSend}
                      disabled={ticket?.status === 'CLOSED'}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>
      {/* Profile Modal */}
      {/* <ProfileModal open={profileModalOpen} onClose={() => setProfileModalOpen(false)} userId={ticket.userId._id} /> */}
      <ProfileModal open={profileModalOpen} onClose={handleProfileModalClose} user={ticket.userId} />
    </>
  );
};

export default SupportTicket;
